import React, { useRef, useState } from "react";
import api, { BACKEND_URL, formatApiError } from "../../lib/api";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

// ImageUpload: lets user upload an image; sets the URL via onChange.
// `value` is a string — either a relative `/api/files/...` returned by upload, or any external URL.
const ImageUpload = ({ value, onChange, label = "Image", testId }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const previewSrc = value
    ? value.startsWith("http") ? value : `${BACKEND_URL}${value}`
    : "";

  return (
    <div data-testid={testId}>
      {label && <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-1.5">{label}</label>}
      <div className="flex items-start gap-3">
        <div className="w-32 h-24 border border-zinc-200 rounded-md bg-zinc-50 flex items-center justify-center overflow-hidden flex-shrink-0">
          {previewSrc ? (
            <img src={previewSrc} alt="preview" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={20} className="text-zinc-300" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Image URL or upload"
            className="w-full px-3 py-2 border border-zinc-200 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-500 text-sm"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onPick}
              disabled={busy}
              className="text-sm font-semibold inline-flex items-center gap-2 px-3 py-1.5 border border-zinc-200 rounded-md hover:border-orange-500 disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {busy ? "Uploading…" : "Upload image"}
            </button>
            {value && (
              <button type="button" onClick={() => onChange("")} className="text-sm text-red-600 inline-flex items-center gap-1 hover:underline">
                <X size={14} /> Clear
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
